/**
 * COMMERCE-WORKSPACE-000002 — Finance founder workspace.
 * SoT: MarketSync domain=finance. No P&L invention.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type FinanceEventPage as FinancePageResult,
  type FinanceEventRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { FounderCommerceShell } from './workspace/FounderCommerceShell';
import { OrderLink, SettlementLink, WorkspaceLegend } from './workspace/commerceFields';
import { useCommerceDomainHealth } from './workspace/useCommerceDomainHealth';

export function MarketSyncFinancePage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params] = useSearchParams();

  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(25);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [sortCol, setSortCol] = useState('posted_date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [eventFilter, setEventFilter] = useState('');
  const [result, setResult] = useState<FinancePageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [bootstrapped, setBootstrapped] = useState(false);

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  useEffect(() => {
    if (bootstrapped) return;
    const uq = params.get('q') || '';
    if (uq) {
      setQ(uq);
      setQDraft(uq);
    }
    setBootstrapped(true);
  }, [params, bootstrapped]);

  const connection = connections.find((c) => c.id === connectionId);
  const health = useCommerceDomainHealth(connectionId, 'finance');
  const sortParam = order === 'desc' ? `${sortCol}_desc` : sortCol;

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.listFinanceEventsPage(connectionId, {
        page,
        page_size: pageSize,
        q: q || undefined,
        sort: sortParam,
        order,
        status: eventFilter || undefined,
      });
      setLatencyMs(Math.round(performance.now() - t0));
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load finance events');
      setResult(null);
      setLatencyMs(undefined);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, q, sortParam, order, eventFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = result?.items ?? [];
  const meta: DataGridMeta | null = result
    ? {
        total_count: result.total_count,
        returned_count: result.returned_count ?? result.count,
        page: result.page,
        page_size: result.page_size,
        pages: result.pages,
        remaining: result.remaining,
        has_more: result.has_more,
        execution_time_ms: result.execution_time_ms ?? latencyMs,
        source: result.source,
        data_source_badge: result.source || 'MarketSync · finance',
        last_sync: health.lastSync || connection?.last_sync_at,
        amazon_count: health.amazonCount,
      }
    : null;

  const columns: DataGridColumn<FinanceEventRow>[] = useMemo(
    () => [
      {
        id: 'event_id',
        header: 'Event ID',
        width: 200,
        stickyLeft: 0,
        render: (r) => <span className="font-mono text-[10px]">{r.event_id}</span>,
        exportValue: (r) => r.event_id,
      },
      {
        id: 'event_type',
        header: 'Event type',
        width: 120,
        sortable: true,
        render: (r) => (
          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium dark:bg-slate-800">
            {r.event_type ?? '—'}
          </span>
        ),
        exportValue: (r) => r.event_type ?? '',
      },
      {
        id: 'amount',
        header: 'Amount',
        width: 100,
        sortable: true,
        render: (r) => <span className="font-mono tabular-nums">{r.amount ?? '—'}</span>,
        exportValue: (r) => r.amount ?? '',
      },
      {
        id: 'currency',
        header: 'CCY',
        width: 56,
        render: (r) => r.currency ?? '—',
        exportValue: (r) => r.currency ?? '',
      },
      {
        id: 'posted_date',
        header: 'Posted',
        width: 150,
        sortable: true,
        render: (r) => <span className="font-mono text-[10px]">{r.posted_date ?? '—'}</span>,
        exportValue: (r) => r.posted_date ?? '',
      },
      {
        id: 'order_id',
        header: 'Order',
        width: 150,
        sortable: true,
        render: (r) => <OrderLink orderId={r.order_id} connectionId={connectionId} />,
        exportValue: (r) => r.order_id ?? '',
      },
      {
        id: 'settlement_id',
        header: 'Settlement',
        width: 130,
        render: (r) => <SettlementLink settlementId={r.settlement_id} connectionId={connectionId} />,
        exportValue: (r) => r.settlement_id ?? '',
      },
      {
        id: 'fee_type',
        header: 'Fee type',
        width: 110,
        render: (r) => r.fee_type || '—',
        exportValue: (r) => r.fee_type ?? '',
      },
      {
        id: 'marketplace_id',
        header: 'Marketplace',
        width: 100,
        render: (r) => <span className="font-mono">{r.marketplace_id || '—'}</span>,
        exportValue: (r) => r.marketplace_id ?? '',
      },
      {
        id: 'raw_reference',
        header: 'Raw ref',
        width: 120,
        render: (r) => (
          <span className="font-mono text-[10px]">{r.raw_reference || r.source_raw_id || '—'}</span>
        ),
        exportValue: (r) => r.raw_reference ?? r.source_raw_id ?? '',
      },
    ],
    [connectionId],
  );

  return (
    <FounderCommerceShell
      domain="finance"
      title="Finance"
      subtitle="Financial events ledger. Settlement IDs search this grid. No profit/GST/analytics invention."
      entryKey="Financial Event"
      connection={connection}
      domainBadge={health.quality || 'finance'}
      legend={<WorkspaceLegend />}
    >
      <PlatformDataGrid
        gridId="finance-v2"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={rows}
        rowKey={(r) => r.event_id}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage="No finance events. Run Finance sync (domain=finance) from Connect, then refresh."
        emptyAction={
          <Link to="/amazon/connect" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
            Open Connect
          </Link>
        }
        page={page}
        pageSize={pageSize}
        sort={sortCol}
        order={order}
        searchDraft={qDraft}
        searchApplied={q || undefined}
        onSearchDraftChange={setQDraft}
        onSearch={() => {
          setQ(qDraft.trim());
          setPage(1);
        }}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onSortChange={(col, ord) => {
          setSortCol(col);
          setOrder(ord);
          setPage(1);
        }}
        onRefresh={() => void load()}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        density="compact"
        toolbarExtra={
          <>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Connection</span>
              <select
                className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
                value={connectionId}
                onChange={(e) => {
                  setConnectionId(e.target.value);
                  setPage(1);
                  setSelected(new Set());
                }}
              >
                {connections.length === 0 && <option value="">No connections</option>}
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-500">Event type</span>
              <select
                className="mt-1 block min-h-11 min-w-[10rem] rounded-lg border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
                value={eventFilter}
                onChange={(e) => {
                  setEventFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                <option value="Shipment">Shipment</option>
                <option value="Refund">Refund</option>
                <option value="Adjustment">Adjustment</option>
                <option value="ServiceFee">ServiceFee</option>
              </select>
            </label>
          </>
        }
      />
    </FounderCommerceShell>
  );
}
